import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { storage } from "./firebase"

/** ~250 MB — enough for ~90s phone 1080p/4K at typical bitrates; 50 MB was ~4.5 Mbps avg (too tight for 4K). */
export const STUDIO_WALKTHROUGH_MAX_BYTES = 250 * 1024 * 1024

const ALLOWED_VIDEO_MIME_TYPES = new Set([
  "video/mp4",
  "video/quicktime",
  "video/webm",
])

const ALLOWED_VIDEO_EXTENSIONS = new Set([".mp4", ".mov", ".webm"])

function sanitizeFileNameForStorage(fileName: string): string {
  return fileName
    .replace(/[^a-zA-Z0-9.-]/g, "_")
    .replace(/\.+/g, ".")
    .replace(/^\./, "")
    .substring(0, 100)
}

export type StudioWalkthroughValidation =
  | { isValid: true }
  | { isValid: false; errorMessage: string }

export function validateStudioWalkthroughVideoFile(file: File): StudioWalkthroughValidation {
  if (file.size > STUDIO_WALKTHROUGH_MAX_BYTES) {
    return {
      isValid: false,
      errorMessage: `File is too large. Maximum size is ${STUDIO_WALKTHROUGH_MAX_BYTES / 1024 / 1024} MB.`,
    }
  }

  const hasValidMimeType = file.type === "" || ALLOWED_VIDEO_MIME_TYPES.has(file.type)

  const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf("."))
  const hasValidExtension = ALLOWED_VIDEO_EXTENSIONS.has(fileExtension)

  if (!hasValidMimeType && !hasValidExtension) {
    return {
      isValid: false,
      errorMessage: 'Please use MP4, MOV, or WebM.',
    }
  }

  if (!hasValidExtension) {
    return {
      isValid: false,
      errorMessage: `Allowed extensions: ${Array.from(ALLOWED_VIDEO_EXTENSIONS).join(", ")}`,
    }
  }

  if (file.name.includes("../") || file.name.includes("..\\")) {
    return { isValid: false, errorMessage: "Invalid file name." }
  }

  return { isValid: true }
}

/**
 * Firestore may contain empty strings, placeholders, or non-string types.
 * Only a real http(s) URL counts as a stored walkthrough for UI (preview + remove).
 */
export function parsePersistedStudioWalkthroughVideoUrl(raw: unknown): string | undefined {
  if (raw == null) return undefined
  if (typeof raw !== "string") return undefined
  const t = raw.trim()
  if (!t || t === "null" || t === "undefined") return undefined
  try {
    const u = new URL(t)
    if (u.protocol !== "http:" && u.protocol !== "https:") return undefined
    return u.href
  } catch {
    return undefined
  }
}

export async function uploadStudioWalkthroughVideo(
  file: File,
  artistId: string,
  userId: string
): Promise<string> {
  if (!userId?.trim()) {
    throw new Error("You must be signed in to upload a video.")
  }

  const validation = validateStudioWalkthroughVideoFile(file)
  if (!validation.isValid) {
    throw new Error(validation.errorMessage)
  }

  if (artistId.includes("../") || artistId.includes("..\\")) {
    throw new Error("Invalid artist.")
  }

  const timestamp = Date.now()
  const sanitizedBaseName = sanitizeFileNameForStorage(file.name)
  const storagePath = `studio-walkthrough/${artistId}/${timestamp}-${sanitizedBaseName}`
  const storageRef = ref(storage, storagePath)

  const metadata = {
    contentType: file.type || "video/mp4",
    customMetadata: {
      uploadedBy: userId,
      uploadedAt: new Date().toISOString(),
      originalName: file.name,
    },
  }

  const snapshot = await uploadBytes(storageRef, file, metadata)
  return getDownloadURL(snapshot.ref)
}
